import {toast} from 'react-toastify';

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs']);
const MAX_FTYP_BYTES = 256;

// Detects HEIC/HEIF from the file header rather than `file.type`, which is reported inconsistently
// for HEIC across platforms.
export const isHeicFile = async (file: File): Promise<boolean> => {
  const head = new Uint8Array(await file.slice(0, MAX_FTYP_BYTES).arrayBuffer());
  if (head.length < 16) {
    return false;
  }

  const ascii = (from: number, to: number) => String.fromCharCode(...head.slice(from, to));

  // ISO base media layout: 4-byte box size, then 'ftyp', then major_brand, minor_version,
  // then zero or more 4-byte compatible_brands filling out the rest of the box.
  if (ascii(4, 8) !== 'ftyp') {
    return false;
  }

  const boxSize = new DataView(head.buffer).getUint32(0, false);
  const brandsEnd = Math.min(boxSize, head.length);

  const brands = new Set([ascii(8, 12)]); // major_brand
  for (let offset = 16; offset + 4 <= brandsEnd; offset += 4) {
    brands.add(ascii(offset, offset + 4)); // compatible_brands (skips minor_version at 12-16)
  }

  if (brands.has('avif') || brands.has('avis')) {
    // AVIF is also HEIF-boxed and shares generic brands like 'mif1', but browsers can decode it.
    return false;
  }
  return [...brands].some((brand) => HEIC_BRANDS.has(brand));
};

// Converts a HEIC file to JPEG using the browser's own decoder. Returns null if this browser
// can't decode HEIC (e.g. Chrome/Firefox today) rather than throwing, so callers can fall back
// to guidance instead of a crash.
export const convertHeicToJpeg = async (file: File, maxDimension: number): Promise<File | null> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) {
      return null;
    }

    const filename = `${file.name.replace(/\.[^./]+$/, '')}.jpg`;
    return new File([blob], filename, {type: 'image/jpeg'});
  } finally {
    bitmap.close();
  }
};

// Shown when a photo is HEIC and this browser can't decode it (desktop Chrome/Firefox today), or
// as a backstop when the API rejects an image with a 415. `autoClose: false` since this is
// multi-step guidance the user needs time to read, not a quick status update.
export const showHeicGuidance = (): void => {
  toast.error(
    <div>
      <p className="font-semibold">This photo can&apos;t be uploaded from this browser!</p>
      <p className="mt-1">iPhone photos are saved in HEIC format, which this browser can&apos;t read.</p>
      <p className="mt-2 font-semibold">Any of these will work:</p>
      <ul className="list-disc pl-4">
        <li>Open this page in Safari and upload again.</li>
        <li>
          On your iPhone, go to Settings → Camera → Formats and choose{' '}
          Most Compatible. New photos will be JPEG.
        </li>
        <li>Convert this photo to JPEG first: on a Mac, open it in Preview and choose File → Export, format JPEG.</li>
      </ul>
    </div>,
    {autoClose: false}
  );
};
